<template>
  <div class="relative">
    <!-- Theme Toggle Button -->
    <button
      @click="toggleTheme"
      class="p-2 text-gray-400 dark:text-gray-500 hover:text-gray-500 dark:hover:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 rounded-md transition-colors duration-200"
      :aria-label="`Switch to ${themeStore.currentTheme === 'dark' ? 'light' : 'dark'} mode`"
    >
      <SunIcon v-if="themeStore.currentTheme === 'dark'" class="w-5 h-5" />
      <MoonIcon v-else class="w-5 h-5" />
    </button>

    <!-- Theme Menu Dropdown -->
    <div
      v-if="showMenu"
      class="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 z-50"
    >
      <div class="py-1">
        <button
          @click="setTheme('light')"
          class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center transition-colors duration-200"
          :class="{
            'bg-primary-50 dark:bg-primary-900 text-primary-700 dark:text-primary-300':
              themeStore.theme === 'light',
          }"
        >
          <SunIcon class="w-4 h-4 mr-2" />
          Light
        </button>

        <button
          @click="setTheme('dark')"
          class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center transition-colors duration-200"
          :class="{
            'bg-primary-50 dark:bg-primary-900 text-primary-700 dark:text-primary-300':
              themeStore.theme === 'dark',
          }"
        >
          <MoonIcon class="w-4 h-4 mr-2" />
          Dark
        </button>

        <button
          @click="setTheme('system')"
          class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center transition-colors duration-200"
          :class="{
            'bg-primary-50 dark:bg-primary-900 text-primary-700 dark:text-primary-300':
              themeStore.theme === 'system',
          }"
        >
          <ComputerDesktopIcon class="w-4 h-4 mr-2" />
          System
          <span v-if="themeStore.theme === 'system'" class="ml-auto text-xs text-gray-500 dark:text-gray-400">
            ({{ themeStore.isDark ? 'Dark' : 'Light' }})
          </span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ref, onMounted, onUnmounted } from 'vue'
  import { SunIcon, MoonIcon, ComputerDesktopIcon } from '@heroicons/vue/24/outline'
  import { useThemeStore } from '@/stores/theme'

  // Store
  const themeStore = useThemeStore()

  // Reactive state
  const showMenu = ref(false)

  // Methods
  const toggleTheme = () => {
    showMenu.value = !showMenu.value
  }

  const setTheme = (theme: 'light' | 'dark' | 'system') => {
    themeStore.setTheme(theme)
    showMenu.value = false
  }

  const closeMenu = (event: Event) => {
    const target = event.target as HTMLElement
    if (!target.closest('.relative')) {
      showMenu.value = false
    }
  }

  // Lifecycle
  onMounted(() => {
    document.addEventListener('click', closeMenu)
  })

  onUnmounted(() => {
    document.removeEventListener('click', closeMenu)
  })
</script>

<style scoped>
  /* Smooth transitions for theme changes */
  .transition-colors {
    transition-property: color, background-color, border-color;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
    transition-duration: 200ms;
  }
</style>
